import React, { useState, useEffect } from 'react';
import api from '../../services/api';

const UsageDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [usageData, setUsageData] = useState(null);
  const [period, setPeriod] = useState('30d');

  useEffect(() => {
    fetchUsageData();
  }, [period]);

  const fetchUsageData = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/api/admin/usage?period=${period}`);
      setUsageData(response.data);
    } catch (error) {
      console.error('Failed to fetch usage data:', error);
      // Mock data
      setUsageData({
        summary: {
          api_calls: 125847,
          active_users: 45,
          leads_processed: 3420,
          predictions_made: 8934,
        },
        limits: {
          api_calls: { used: 125847, limit: 500000, percentage: 25.2 },
          storage: { used: 2.4, limit: 10, unit: 'GB', percentage: 24 },
          users: { used: 45, limit: 100, percentage: 45 },
          ml_predictions: { used: 8934, limit: 50000, percentage: 17.9 },
        },
        trends: [
          { date: 'Week 1', api_calls: 28000, users: 38 },
          { date: 'Week 2', api_calls: 31000, users: 42 },
          { date: 'Week 3', api_calls: 33000, users: 44 },
          { date: 'Week 4', api_calls: 33847, users: 45 },
        ],
        topFeatures: [
          { name: 'Lead Scoring', usage: 3420, percentage: 38 },
          { name: 'Churn Prediction', usage: 2150, percentage: 24 },
          { name: 'Email Automation', usage: 1890, percentage: 21 },
          { name: 'Reports', usage: 980, percentage: 11 },
          { name: 'Integrations', usage: 540, percentage: 6 },
        ],
        topUsers: [
          { name: 'John Smith', role: 'Sales Manager', actions: 4520 },
          { name: 'Sarah Johnson', role: 'Marketing Lead', actions: 3890 },
          { name: 'Mike Wilson', role: 'Sales Rep', actions: 2340 },
          { name: 'Emily Davis', role: 'Admin', actions: 1980 },
          { name: 'Tom Brown', role: 'Sales Rep', actions: 1450 },
        ],
      });
    } finally {
      setLoading(false);
    }
  };

  const getUsageColor = (percentage) => {
    if (percentage >= 90) return 'var(--red-500)';
    if (percentage >= 70) return 'var(--yellow-500)';
    return 'var(--green-500)';
  };

  if (loading || !usageData) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <div
          style={{
            width: '40px',
            height: '40px',
            border: '3px solid var(--gray-200)',
            borderTopColor: 'var(--primary-600)',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
          }}
        />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
        <div>
          <h1 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--gray-900)' }}>
            📊 Usage Dashboard
          </h1>
          <p style={{ margin: 0, marginTop: '4px', color: 'var(--gray-500)' }}>
            Monitor your plan usage and feature adoption
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          {['7d', '30d', '90d'].map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              style={{
                padding: '8px 16px',
                border: 'none',
                borderRadius: '6px',
                fontSize: '0.875rem',
                fontWeight: 500,
                cursor: 'pointer',
                backgroundColor: period === p ? 'var(--primary-600)' : 'var(--gray-100)',
                color: period === p ? 'white' : 'var(--gray-600)',
                transition: 'all 0.2s',
              }}
            >
              {p === '7d' ? '7 Days' : p === '30d' ? '30 Days' : '90 Days'}
            </button>
          ))}
        </div>
      </div>

      {/* Summary Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>
        <div className="card" style={{ padding: '1.5rem', textAlign: 'center' }}>
          <p style={{ margin: 0, fontSize: '2rem', fontWeight: 600, color: 'var(--primary-600)' }}>
            {usageData.summary.api_calls.toLocaleString()}
          </p>
          <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--gray-500)' }}>API Calls</p>
        </div>
        <div className="card" style={{ padding: '1.5rem', textAlign: 'center' }}>
          <p style={{ margin: 0, fontSize: '2rem', fontWeight: 600, color: 'var(--green-600)' }}>
            {usageData.summary.active_users}
          </p>
          <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--gray-500)' }}>Active Users</p>
        </div>
        <div className="card" style={{ padding: '1.5rem', textAlign: 'center' }}>
          <p style={{ margin: 0, fontSize: '2rem', fontWeight: 600, color: 'var(--blue-600)' }}>
            {usageData.summary.leads_processed.toLocaleString()}
          </p>
          <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--gray-500)' }}>Leads Processed</p>
        </div>
        <div className="card" style={{ padding: '1.5rem', textAlign: 'center' }}>
          <p style={{ margin: 0, fontSize: '2rem', fontWeight: 600, color: 'var(--purple-600)' }}>
            {usageData.summary.predictions_made.toLocaleString()}
          </p>
          <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--gray-500)' }}>ML Predictions</p>
        </div>
      </div>

      {/* Usage Limits */}
      <div className="card" style={{ padding: '24px', marginBottom: '24px' }}>
        <h3 style={{ margin: 0, marginBottom: '16px', color: 'var(--gray-900)' }}>Plan Limits</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '24px' }}>
          {Object.entries(usageData.limits).map(([key, data]) => (
            <div key={key}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontSize: '0.875rem', textTransform: 'capitalize', color: 'var(--gray-700)' }}>
                  {key.replace(/_/g, ' ')}
                </span>
                <span style={{ fontSize: '0.875rem', color: 'var(--gray-500)' }}>
                  {data.used.toLocaleString()} / {data.limit.toLocaleString()}
                  {data.unit ? ` ${data.unit}` : ''}
                </span>
              </div>
              <div
                style={{
                  width: '100%',
                  height: '8px',
                  backgroundColor: 'var(--gray-200)',
                  borderRadius: '4px',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    width: `${data.percentage}%`,
                    height: '100%',
                    backgroundColor: getUsageColor(data.percentage),
                    borderRadius: '4px',
                    transition: 'width 0.3s',
                  }}
                />
              </div>
              <p style={{ margin: 0, marginTop: '4px', fontSize: '0.75rem', color: 'var(--gray-500)' }}>
                {data.percentage.toFixed(1)}% used
              </p>
            </div>
          ))}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
        {/* Top Features */}
        <div className="card" style={{ padding: '24px' }}>
          <h3 style={{ margin: 0, marginBottom: '16px', color: 'var(--gray-900)' }}>🏆 Top Features</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {usageData.topFeatures.map((feature, idx) => (
              <div key={idx}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <span style={{ fontSize: '0.875rem', color: 'var(--gray-700)' }}>{feature.name}</span>
                  <span style={{ fontSize: '0.875rem', color: 'var(--gray-500)' }}>
                    {feature.usage.toLocaleString()} uses
                  </span>
                </div>
                <div
                  style={{
                    width: '100%',
                    height: '6px',
                    backgroundColor: 'var(--gray-200)',
                    borderRadius: '3px',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      width: `${feature.percentage}%`,
                      height: '100%',
                      backgroundColor: 'var(--primary-500)',
                      borderRadius: '3px',
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Top Users */}
        <div className="card" style={{ padding: '24px' }}>
          <h3 style={{ margin: 0, marginBottom: '16px', color: 'var(--gray-900)' }}>👤 Most Active Users</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {usageData.topUsers.map((user, idx) => (
              <div
                key={idx}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '8px 12px',
                  backgroundColor: idx === 0 ? 'var(--yellow-50)' : 'var(--gray-50)',
                  borderRadius: '8px',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--gray-400)', width: '24px' }}>
                    {idx + 1}
                  </span>
                  <div>
                    <p style={{ margin: 0, fontWeight: 500, color: 'var(--gray-900)' }}>{user.name}</p>
                    <p style={{ margin: 0, fontSize: '0.75rem', color: 'var(--gray-500)' }}>{user.role}</p>
                  </div>
                </div>
                <span
                  style={{
                    padding: '4px 8px',
                    backgroundColor: 'var(--primary-100)',
                    borderRadius: '4px',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    color: 'var(--primary-700)',
                  }}
                >
                  {user.actions.toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Upgrade Banner */}
      <div
        className="card"
        style={{
          padding: '24px',
          marginTop: '24px',
          background: 'linear-gradient(135deg, var(--primary-600), var(--primary-700))',
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div>
          <h3 style={{ margin: 0, marginBottom: '4px' }}>Need more capacity?</h3>
          <p style={{ margin: 0, opacity: 0.9, fontSize: '0.875rem' }}>
            Upgrade your plan to unlock higher limits and additional features
          </p>
        </div>
        <button
          style={{
            padding: '10px 24px',
            border: 'none',
            borderRadius: '6px',
            backgroundColor: 'white',
            color: 'var(--primary-700)',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Upgrade Plan
        </button>
      </div>
    </div>
  );
};

export default UsageDashboard;
